/**
 * API client.
 *
 * Every call is same-origin through CloudFront, so it is not a CORS request and the
 * refresh cookie is sent automatically. Calling the API Gateway domain directly would
 * silently drop that cookie (spec §8.3).
 */
import { loadConfig } from './config'

export interface Problem {
  type: string
  title: string
  status: number
  detail: string
  tier?: string
  limit?: number
  current?: number
  upgradeUrl?: string
  errors?: { field: string; rule: string }[]
}

export class ApiError extends Error {
  readonly problem: Problem

  constructor(problem: Problem) {
    super(problem.detail || problem.title)
    this.name = 'ApiError'
    this.problem = problem
  }

  /** 402 means "you cannot afford this", which the UI answers with an upgrade prompt
   *  rather than an error (spec §6.3). */
  get isQuota(): boolean {
    return this.problem.status === 402
  }
}

/** Access tokens live in memory only — never in localStorage (spec §8.3). */
let accessToken: string | null = null
let onUnauthenticated: (() => void) | null = null

export function setAccessToken(token: string | null): void {
  accessToken = token
}

export function getAccessToken(): string | null {
  return accessToken
}

export function setUnauthenticatedHandler(handler: (() => void) | null): void {
  onUnauthenticated = handler
}

async function refresh(): Promise<boolean> {
  const { apiBaseUrl } = await loadConfig()
  const response = await fetch(`${apiBaseUrl}/auth/refresh`, {
    method: 'POST',
    credentials: 'same-origin',
  })
  if (!response.ok) return false
  const body = (await response.json()) as { accessToken: string }
  accessToken = body.accessToken
  return true
}

export async function request<T>(
  path: string,
  options: { method?: string; body?: unknown; contentType?: string; retry?: boolean } = {},
): Promise<T> {
  const { apiBaseUrl } = await loadConfig()
  const { method = 'GET', body, contentType = 'application/json', retry = true } = options

  const headers: Record<string, string> = {}
  if (accessToken) headers.Authorization = `Bearer ${accessToken}`
  if (body !== undefined) headers['Content-Type'] = contentType

  const response = await fetch(`${apiBaseUrl}${path}`, {
    method,
    headers,
    credentials: 'same-origin',
    body: body === undefined ? undefined : typeof body === 'string' ? body : JSON.stringify(body),
  })

  if (response.status === 401 && retry) {
    // One attempt to mint a new access token from the refresh cookie; a second failure
    // means the session is genuinely over.
    if (await refresh()) return request<T>(path, { ...options, retry: false })
    onUnauthenticated?.()
  }

  if (response.status === 204) return undefined as T
  const payload = await response.json().catch(() => ({}))
  if (!response.ok) {
    throw new ApiError({
      type: 'about:blank',
      title: 'Request failed',
      status: response.status,
      detail: 'The request could not be completed.',
      ...(payload as object),
    })
  }
  return payload as T
}

export const api = {
  me: () => request<Profile>('/me'),
  deleteAccount: () => request<{ deleted: boolean }>('/me', { method: 'DELETE' }),
  listDecks: () => request<{ decks: DeckSummary[]; count: number }>('/decks'),
  getDeck: (id: string) => request<Deck>(`/decks/${id}`),
  createDeck: (document: string, contentType: string) =>
    request<Deck>('/decks', { method: 'POST', body: document, contentType }),
  deleteDeck: (id: string) => request<{ deleted: boolean }>(`/decks/${id}`, { method: 'DELETE' }),
  unlockDeck: (id: string) => request<DeckSummary>(`/decks/${id}/unlock`, { method: 'POST' }),
  unarchiveDeck: (id: string) => request<Deck>(`/decks/${id}/unarchive`, { method: 'POST' }),
  attempts: (id: string) => request<{ attempts: Attempt[] }>(`/decks/${id}/attempts`),
  listImages: () => request<{ images: ImageSummary[]; count: number }>('/images'),
  imageVariants: (name: string) => request<ImageVariants>(`/images/${name}`),
  deleteImage: (name: string) =>
    request<{ deleted: boolean }>(`/images/${name}`, { method: 'DELETE' }),
  requestUpload: (payload: { name: string; contentType: string; sizeBytes: number }) =>
    request<PresignedUpload>('/images', { method: 'POST', body: payload }),
  startSession: (
    payload:
      | { deckId: string; mode: string; portion: string; shuffle: boolean }
      | { scope: 'all'; mode: 'test'; count: AllDecksCount },
  ) =>
    request<SessionState>('/sessions', { method: 'POST', body: payload }),
  session: (id: string) => request<SessionState>(`/sessions/${id}`),
  answer: (id: string, payload: { cardId: string; selfGrade?: string; choiceIndex?: number }) =>
    request<AnswerResult>(`/sessions/${id}/answer`, { method: 'POST', body: payload }),
  complete: (id: string) => request<SessionScore>(`/sessions/${id}/complete`, { method: 'POST' }),
  checkout: (tier: string) =>
    request<{ url: string }>('/billing/checkout', { method: 'POST', body: { tier } }),
  portal: () => request<{ url: string }>('/billing/portal', { method: 'POST' }),
  listConnectedApps: () => request<{ items: ConnectedApp[] }>('/account/connected-apps'),
  revokeConnectedApp: (clientId: string) =>
    request<void>(connectedAppPath(clientId), { method: 'DELETE' }),
}

/** The one place a connected app's URL is built. A client id can be a URL (CIMD clients,
 *  e.g. https://example.com/oauth/client.json), so it is always percent-encoded. If the
 *  API changes how it addresses a client (opaque id, query parameter), change only this. */
export function connectedAppPath(clientId: string): string {
  return `/account/connected-apps/${encodeURIComponent(clientId)}`
}

/** Hand-written to match the connected-apps API contract (not generated from openapi.json). */
export interface ConnectedApp {
  clientId: string
  name: string
  verified: boolean
  domain: string | null
  authorisedAt: string
  lastUsedAt: string | null
}

export interface Profile {
  userId: string
  tier: string
  subscriptionStatus?: string
  usage: {
    decks: number
    deckLimit: number
    images: number
    imageLimit: number
    imageBytes: number
    imageByteLimit: number
  }
  createdAt: string
}

export type DeckStatus = 'ACTIVE' | 'FLAGGED' | 'LOCKED' | 'ARCHIVED'

export interface DeckSummary {
  deckId: string
  title: string
  description: string
  tags: string[]
  cardCount: number
  status: DeckStatus
  createdAt: string
  updatedAt: string
  lastAccessedAt: string
  flagDueAt?: string
}

export interface Card {
  id: string
  front: string
  back: string
  hint?: string
  image?: string
}

export interface Deck extends DeckSummary {
  cards: Card[]
  /** Images dropped on restore because they no longer exist (unarchive only). */
  missingImages?: string[]
}

/** Cards in an all-decks test (spec §6.2). */
export type AllDecksCount = 10 | 25 | 50

export interface SessionCard {
  id: string
  front: string
  hint?: string
  image?: string
  back?: string | null
  choices?: string[] | null
  /** Set only in an all-decks test, where the cards come from several decks. */
  deckId?: string | null
  deckTitle?: string | null
}

export interface SessionState {
  sessionId: string
  scope?: 'deck' | 'all'
  /** Null in an all-decks test. */
  deckId?: string | null
  mode: 'learn' | 'test'
  portion: string
  status: 'IN_PROGRESS' | 'COMPLETE'
  currentIndex: number
  total: number
  answered: number
  card: SessionCard | null
  expiresAt: number
}

export interface AnswerResult {
  cardId: string
  correct: boolean
  back: string
  correctChoiceIndex?: number | null
  answered: number
  total: number
  next: SessionCard | null
}

export interface SessionScore {
  sessionId: string
  scope?: 'deck' | 'all'
  deckId?: string | null
  correct: number
  total: number
  percent: number
  durationSeconds: number
  completedAt: string
  breakdown: {
    cardId: string
    front: string
    back: string
    correct: boolean
    deckId?: string | null
    deckTitle?: string | null
  }[]
}

export interface Attempt {
  deckId: string
  sessionId: string
  mode: string
  portion: string
  correct: number
  total: number
  percent: number
  durationSeconds: number
  completedAt: string
}

export interface ImageSummary {
  name: string
  status: 'PENDING' | 'READY' | 'FAILED'
  contentType: string
  originalBytes: number
  derivedBytes: number
  createdAt: string
  /** Active decks that reference the image; archived decks are not listed. */
  usedBy: { deckId: string; title: string }[]
}

export interface ImageVariants {
  name: string
  variants: Record<string, string>
  expiresIn: number
}

export interface PresignedUpload {
  name: string
  url: string
  fields: Record<string, string>
  expiresIn: number
}
